import React, {
	useState, useEffect, useLayoutEffect, useCallback, useRef,
} from 'react';
import CustomIcon from '../Common/CustomIcon/CustomIcon';
import {
	broncoURL,
} from 'Vulture/ENV.js';
import {
	getThumbnailPath,
} from './Chonky/lib';
import {
	getSession,
} from 'Vulture/Session.js';
import styles from './InFolderScheduling.module.css';
import {
	toDateStr,
	parseDate,
	addDays,
	diffDays,
	formatBar,
	isExpiredRange,
	getFileType,
	wouldOverlap,
} from './utils/inFolderScheduling';
import {
	defaultSorter,
	normalizeOrder,
} from './utils/slides';

const VIEW_MODES = {
	WEEKLY:   { label: 'WEEKLY – 2 WEEKS',   days: 14, colDays: 1 },
	MONTHLY:  { label: 'MONTHLY – 2 MONTHS', days: 60, colDays: 3 },
};

const RemoveAllSchedulingModal = ({
	onUndo, onDeactivate, onProceed,
}) => (
	<div className={styles.overlayBackdrop}>
		<div className={styles.modal}>
			<div className={styles.modalTitle}>Removing all scheduling</div>
			<p className={styles.modalBody}>
				Removing all scheduling will keep this slide active at all times.
				Please choose an option.
			</p>
			<div className={styles.modalButtons}>
				<button type="button" className={styles.modalBtnUndo}       onClick={onUndo}>Undo</button>
				<button type="button" className={styles.modalBtnDeactivate} onClick={onDeactivate}>Deactivate</button>
				<button type="button" className={styles.modalBtnProceed}    onClick={onProceed}>Proceed</button>
			</div>
		</div>
	</div>
);

const MIN_BAR_DAYS = 1;

const GanttBar = ({
	range, index, fileId, colWidth, timelineStartDate, viewDays,
	onMove, onResizeLeft, onResizeRight, onDelete, onDragEnd,
}) => {
	const dragState = useRef(null);

	useEffect(() => () => {
		dragState.current?.cleanup?.();
	}, []);

	// All hooks must be declared before any conditional return (Rules of Hooks).
	// attachDrag is declared here unconditionally; parseDate / layout math follow.
	const attachDrag = useCallback((type) => ({
		onMouseDown(e) {
			e.preventDefault();
			e.stopPropagation();
			dragState.current = {
				type,
				startX:    e.clientX,
				origStart: range.start_date,
				origEnd:   range.end_date,
				hasMoved:  false,
			};

			const onMouseMove = (ev) => {
				dragState.current.hasMoved = true;
				const dx    = ev.clientX - dragState.current.startX;
				const dDays = Math.round(dx / colWidth);
				const ds    = parseDate(dragState.current.origStart);
				const de    = parseDate(dragState.current.origEnd);

				const minDate = timelineStartDate;
				const maxDate = addDays(timelineStartDate, viewDays - 1);

				if (type === 'move') {
					const dur = diffDays(ds, de);
					let ns = addDays(ds, dDays);
					let ne = addDays(de, dDays);
					if (ns < minDate) { ns = minDate; ne = addDays(ns, dur); }
					if (ne > maxDate) { ne = maxDate; ns = addDays(ne, -dur); }
					onMove(fileId, index, toDateStr(ns), toDateStr(ne));
				} else if (type === 'left') {
					let ns = addDays(ds, dDays);
					if (ns < minDate) ns = minDate;
					if (diffDays(ns, de) + 1 >= MIN_BAR_DAYS) {
						onResizeLeft(fileId, index, toDateStr(ns));
					}
				} else if (type === 'right') {
					let ne = addDays(de, dDays);
					if (ne > maxDate) ne = maxDate;
					if (diffDays(ds, ne) + 1 >= MIN_BAR_DAYS) {
						onResizeRight(fileId, index, toDateStr(ne));
					}
				}
			};

			const cleanup = () => {
				window.removeEventListener('mousemove', onMouseMove);
				window.removeEventListener('mouseup', onUp);
			};
			dragState.current.cleanup = cleanup;

			const onUp = () => {
				const hasMoved = dragState.current?.hasMoved ?? false;
				cleanup();
				dragState.current = null;
				onDragEnd?.(hasMoved);
			};

			window.addEventListener('mousemove', onMouseMove);
			window.addEventListener('mouseup', onUp);
		},
	}), [colWidth, fileId, index, range, onMove, onResizeLeft, onResizeRight, onDragEnd]);

	const startDate = parseDate(range.start_date);
	const endDate   = parseDate(range.end_date);
	if (!startDate || !endDate) return null;

	const startOff  = diffDays(timelineStartDate, startDate);
	const endOff    = diffDays(timelineStartDate, endDate) + 1;
	const visStart  = Math.max(0, Math.min(startOff, viewDays));
	const visEnd    = Math.max(0, Math.min(endOff,   viewDays));

	const leftDays  = visStart;
	const widthDays = Math.max(MIN_BAR_DAYS, visEnd - visStart);
	const left      = leftDays  * colWidth;
	const width     = widthDays * colWidth;
	const expired   = isExpiredRange(range.end_date);

	if (visEnd <= visStart) return null;

	return (
		<div
			className={`${styles.ganttBar} ${expired ? styles.ganttBarExpired : ''}`}
			style={{
				left:  `${left}px`,
				width: `${width}px`,
			}}
			title={expired ? `Expired: ${formatBar(range.start_date, range.end_date)}` : formatBar(range.start_date, range.end_date)}
			{...attachDrag('move')}
		>
			<div className={styles.barResizeLeft} {...attachDrag('left')} />

			<button
				type="button"
				className={styles.barDeleteBtn}
				onClick={(e) => {
					e.stopPropagation();
					onDelete(fileId, index);
				}}
				title="Remove this date range"
			>
				<CustomIcon icon="times-circle" />
			</button>

			<span className={styles.barLabel}>
				{expired ? '⚠ Expired' : formatBar(range.start_date, range.end_date)}
			</span>

			<div className={styles.barResizeRight} {...attachDrag('right')} />
		</div>
	);
};

const InFolderScheduling = (props) => {
	const {
		accountUUID,
		slideDeck,
		folderSlide,
		onBack,
		onBackToList,
		saveRef,
		onDirtyChange,
		isInFolderSchedulingEnabled,
	} = props;

	const [viewMode,      setViewMode]      = useState('MONTHLY');
	const [timelineStart, setTimelineStart] = useState(() => {
		const d = new Date();
		d.setHours(0, 0, 0, 0);
		d.setDate(d.getDate() - 7);
		return d;
	});

	const [files,              setFiles]              = useState([]);
	const [scheduleMap,        setScheduleMap]        = useState({});
	const [loading,            setLoading]            = useState(true);
	const [saving,             setSaving]             = useState(false);
	const [containerWidth,     setContainerWidth]     = useState(0);

	const [removeAllModal,     setRemoveAllModal]     = useState(null);
	const [saveError,          setSaveError]          = useState(null);
	const [loadError,          setLoadError]          = useState(null);

	const config        = VIEW_MODES[viewMode];
	const viewDays      = config.days;
	const colDays       = config.colDays;
	const baseColWidth  = viewMode === 'WEEKLY' ? 64 : 20;
	const STICKY_WIDTH  = 332;
	const colWidth      = containerWidth > STICKY_WIDTH
		? Math.max(baseColWidth, Math.floor((containerWidth - STICKY_WIDTH) / viewDays))
		: baseColWidth;

	const handleSaveRef = useRef(null);
	const justDraggedRef = useRef(false);

	const handleDragEnd = useCallback((hasMoved) => {
		if (!hasMoved) return;
		justDraggedRef.current = true;
		setTimeout(() => { justDraggedRef.current = false; }, 0);
	}, []);

	useEffect(() => {
		if (!folderSlide) return;
		loadData();
	}, [folderSlide?.slideDeckSlideID, folderSlide?.id, slideDeck?.id, accountUUID, isInFolderSchedulingEnabled]);

	const loadData = async () => {
		setLoading(true);
		setLoadError(null);
		try {
			const session = getSession().session;

			const folderFiles = folderSlide?.folderFiles || folderSlide?.files || [];

			const sorted = [...folderFiles].sort(defaultSorter);
			setFiles(sorted);

			const slideDeckSlideID = folderSlide?.slideDeckSlideID || folderSlide?.id;
			const res = await fetch(
				`${broncoURL}/slide_deck/${slideDeck.id}/folder/${slideDeckSlideID}/schedules?account_uuid=${encodeURIComponent(accountUUID)}`,
				{
					headers: {
						'X-Auth-Session': session,
					},
				},
			);

			// S13: Surface GET failures as an inline error rather than silently showing
			// all files as Unscheduled (which misleads users without VCL view permission).
			if (!res.ok) {
				const status = res.status;
				const msg = status === 403
					? 'You do not have permission to view schedules for this folder.'
					: `Failed to load schedules (${status}). Please refresh and try again.`;
				setLoadError(msg);
				return;
			}

			const existing = await res.json();

			const existingByFileId = {};
			(existing || []).forEach((e) => { existingByFileId[String(e.gallery_file_id)] = e; });
			const hasScheduleRows = (existing || []).length > 0;

			const map = {};
			sorted.forEach((f) => {
				const key = String(f.id);
				const row = existingByFileId[key];
				const dateRanges = row ? (row.date_ranges || []) : [];
				const defaultActive = (hasScheduleRows && isInFolderSchedulingEnabled) ? false : true;
				const active     = row ? !!row.active : defaultActive;
				map[key] = {
					active,
					dateRanges,
					_original: JSON.stringify({ active, dateRanges }),
					_dirty:    false,
				};
			});

			setScheduleMap(map);
		} catch (err) {
			console.error('InFolderScheduling load error:', err);
			setLoadError('Failed to load schedules. Please refresh and try again.');
		} finally {
			setLoading(false);
		}
	};

	const dirtyCount = Object.values(scheduleMap).filter((v) => v._dirty).length;

	const updateFile = useCallback((fileId, updater) => {
		setScheduleMap((prev) => {
			const cur = prev[fileId];
			if (!cur) return prev;
			const next = updater(cur);
			const dirty = JSON.stringify({ active: next.active, dateRanges: next.dateRanges }) !== next._original;
			return {
				...prev,
				[fileId]: {
					...next,
					_dirty: dirty,
				},
			};
		});
	}, []);

	const toggleActive = useCallback((fileId) => {
		const key = String(fileId);
		setScheduleMap((prev) => {
			const cur = prev[key];
			if (!cur) return prev;
			const nextActive = !cur.active;
			let nextDateRanges = cur.dateRanges;
			if (nextActive && nextDateRanges.length === 0) {
				// Default schedule starts TOMORROW (PO ruling): activating must never
				// start the file playing immediately — the user sees the bar and can
				// move it, or remove it to make the file always active.
				const tomorrow = addDays(new Date(), 1);
				nextDateRanges = [{
					start_date: toDateStr(tomorrow),
					end_date:   toDateStr(addDays(tomorrow, 6)),
				}];
			}
			const next = { ...cur, active: nextActive, dateRanges: nextDateRanges };
			const dirty = JSON.stringify({ active: next.active, dateRanges: next.dateRanges }) !== next._original;
			return { ...prev, [key]: { ...next, _dirty: dirty } };
		});
	}, []);

	const handleTimelineClick = useCallback((fileId, e) => {
		if (justDraggedRef.current) return;
		if (e.target.closest(`.${styles.ganttBar}`)) return;

		const rect  = e.currentTarget.getBoundingClientRect();
		const relX  = e.clientX - rect.left;
		const dDays = Math.floor(relX / colWidth);
		if (dDays < 0 || dDays >= viewDays) return;
		const lastDay = addDays(timelineStart, viewDays - 1);
		let   start   = addDays(timelineStart, dDays);
		let end = addDays(start, 6);
		if (end > lastDay) {
			end = lastDay;
			const shiftedStart = addDays(lastDay, -6);
			if (shiftedStart >= timelineStart) {
				start = shiftedStart;
			}
		}

		updateFile(fileId, (c) => {
			const newRange = { start_date: toDateStr(start), end_date: toDateStr(end) };
			if (wouldOverlap(c.dateRanges, newRange, -1)) return c;
			return {
				...c,
				dateRanges: [...c.dateRanges, newRange],
			};
		});
	}, [colWidth, timelineStart, viewDays, updateFile]);

	const handleBarMove = useCallback((fileId, idx, newStart, newEnd) => {
		updateFile(fileId, (c) => {
			const testRange = { start_date: newStart, end_date: newEnd };
			if (wouldOverlap(c.dateRanges, testRange, idx)) return c;
			const ranges = [...c.dateRanges];
			ranges[idx] = testRange;
			return {
				...c,
				dateRanges: ranges,
			};
		});
	}, [updateFile]);

	const handleBarResizeLeft = useCallback((fileId, idx, newStart) => {
		updateFile(fileId, (c) => {
			const testRange = { start_date: newStart, end_date: c.dateRanges[idx]?.end_date };
			if (wouldOverlap(c.dateRanges, testRange, idx)) return c;
			const ranges = [...c.dateRanges];
			ranges[idx] = {
				...ranges[idx],
				start_date: newStart,
			};
			return {
				...c,
				dateRanges: ranges,
			};
		});
	}, [updateFile]);

	const handleBarResizeRight = useCallback((fileId, idx, newEnd) => {
		updateFile(fileId, (c) => {
			const testRange = { start_date: c.dateRanges[idx]?.start_date, end_date: newEnd };
			if (wouldOverlap(c.dateRanges, testRange, idx)) return c;
			const ranges = [...c.dateRanges];
			ranges[idx] = {
				...ranges[idx],
				end_date: newEnd,
			};
			return {
				...c,
				dateRanges: ranges,
			};
		});
	}, [updateFile]);

	const handleBarDelete = useCallback((fileId, idx) => {
		const key = String(fileId);
		const cur = scheduleMap[key];
		if (!cur) return;

		const isActive    = cur.active;
		const isLastRange = cur.dateRanges.length === 1;

		if (isActive && isLastRange) {
			setRemoveAllModal({ fileId: key, idx });
			return;
		}

		setScheduleMap((prev) => {
			const c = prev[key];
			if (!c) return prev;
			const ranges = c.dateRanges.filter((_, i) => i !== idx);
			const next   = { ...c, dateRanges: ranges };
			const dirty  = JSON.stringify({ active: next.active, dateRanges: next.dateRanges }) !== next._original;
			return { ...prev, [key]: { ...next, _dirty: dirty } };
		});
	}, [scheduleMap]);

	const resetRow = useCallback((fileId) => {
		setScheduleMap((prev) => {
			const cur = prev[fileId];
			if (!cur) return prev;
			const original = JSON.parse(cur._original);
			return {
				...prev,
				[fileId]: {
					...original,
					_original: cur._original,
					_dirty:    false,
				},
			};
		});
	}, []);

	const handleSave = async () => {
		if (saving || dirtyCount === 0) return;
		setSaving(true);
		setSaveError(null);
		try {
			const session = getSession().session;
			const slideDeckSlideID = folderSlide?.slideDeckSlideID || folderSlide?.id;

			const schedules = files.map((f) => {
				const s = scheduleMap[String(f.id)] || { active: true, dateRanges: [] };
				return {
					gallery_file_id: f.id,
					active:          s.active,
					date_ranges:     s.dateRanges,
				};
			});

			const res = await fetch(
				`${broncoURL}/slide_deck/${slideDeck.id}/folder/${slideDeckSlideID}/schedules`,
				{
					method:  'POST',
					headers: {
						'Content-Type':   'application/json',
						'X-Auth-Session': session,
					},
					body: JSON.stringify({
						account_uuid: accountUUID,
						schedules,
					}),
				},
			);

			if (!res.ok) {
				const text = await res.text().catch(() => '');
				throw new Error(text || `Save failed (${res.status})`);
			}

			setScheduleMap((prev) => {
				const next = { ...prev };
				Object.keys(next).forEach((id) => {
					const cur = next[id];
					next[id] = {
						...cur,
						_original: JSON.stringify({ active: cur.active, dateRanges: cur.dateRanges }),
						_dirty:    false,
					};
				});
				return next;
			});
		} catch (err) {
			console.error('Save error:', err);
			setSaveError(err.message || 'Failed to save schedules. Please try again.');
		} finally {
			setSaving(false);
		}
	};

	handleSaveRef.current = handleSave;

	useEffect(() => {
		if (!saveRef) return;
		saveRef.current = { save: () => handleSaveRef.current() };
		return () => { saveRef.current = null; };
	}, [saveRef]);

	useEffect(() => {
		onDirtyChange?.(dirtyCount, saving);
	}, [dirtyCount, saving]);

	const handleRemoveAllUndo = () => setRemoveAllModal(null);

	const handleRemoveAllDeactivate = () => {
		const { fileId, idx } = removeAllModal;
		updateFile(fileId, (c) => ({
			...c,
			active:     false,
			dateRanges: c.dateRanges.filter((_, i) => i !== idx),
		}));
		setRemoveAllModal(null);
	};

	const handleRemoveAllProceed = () => {
		const { fileId, idx } = removeAllModal;
		updateFile(fileId, (c) => ({
			...c,
			dateRanges: c.dateRanges.filter((_, i) => i !== idx),
		}));
		setRemoveAllModal(null);
	};

	const buildDateColumns = () => {
		const cols = [];
		for (let i = 0; i < viewDays; i += colDays) {
			const d = addDays(timelineStart, i);
			cols.push({
				label:    `${d.getMonth() + 1}/${d.getDate()}`,
				dateStr:  toDateStr(d),
				left:     i * colWidth,
			});
		}
		return cols;
	};

	const dateColumns = buildDateColumns();

	const today = toDateStr(new Date());
	const todayDiff   = diffDays(timelineStart, parseDate(today));
	const todayLeft   = todayDiff * colWidth;
	const totalWidth  = viewDays * colWidth;
	const totalContentWidth = totalWidth + STICKY_WIDTH;

	const timelineRef   = useRef(null);
	const topScrollRef  = useRef(null);
	const viewToggleRef = useRef(null);

	const [sliderProps, setSliderProps] = useState({ left: 3, width: 80 });
	useLayoutEffect(() => {
		const toggle = viewToggleRef.current;
		if (!toggle) return;
		const btns = toggle.querySelectorAll('button');
		const modes = Object.keys(VIEW_MODES);
		const idx = modes.indexOf(viewMode);
		if (btns[idx]) {
			setSliderProps({ left: btns[idx].offsetLeft, width: btns[idx].offsetWidth });
		}
	}, [viewMode, loading]);

	useEffect(() => {
		const top   = topScrollRef.current;
		const table = timelineRef.current;
		if (!top || !table) return;
		const onTop = () => { table.scrollLeft = top.scrollLeft; };
		const onTable = () => { top.scrollLeft = table.scrollLeft; };
		top.addEventListener('scroll', onTop);
		table.addEventListener('scroll', onTable);
		return () => {
			top.removeEventListener('scroll', onTop);
			table.removeEventListener('scroll', onTable);
		};
	}, [loading]);

	useEffect(() => {
		const el = timelineRef.current;
		if (!el) return;
		const measure = () => setContainerWidth(el.clientWidth);
		measure();
		const obs = new ResizeObserver(measure);
		obs.observe(el);
		return () => obs.disconnect();
	}, [loading]);

	const scrollTimeline = (direction) => {
		const stepDays = Math.round(viewDays / 2);
		setTimelineStart((prev) => addDays(prev, direction * stepDays));
		if (timelineRef.current) {
			timelineRef.current.scrollLeft = 0;
		}
		if (topScrollRef.current) {
			topScrollRef.current.scrollLeft = 0;
		}
	};

	const folderLabel  = folderSlide?.label || folderSlide?.description || 'Folder';
	const folderCount  = files.length;

	if (loading) {
		return (
			<div className={styles.loading}>
				<CustomIcon icon="spinner" spin /> Loading schedules…
			</div>
		);
	}

	return (
		<div className={styles.page}>
			{/* Breadcrumb */}
			<div className={styles.breadcrumb}>
				<button type="button" className={styles.breadcrumbLink} onClick={onBackToList || onBack}>
					Slide Decks
				</button>
				<span className={styles.breadcrumbSep}>/</span>
				<button type="button" className={styles.breadcrumbLink} onClick={onBack}>
					{slideDeck.name}
				</button>
				<span className={styles.breadcrumbSep}>/</span>
				<span className={styles.breadcrumbCurrent}>{folderLabel} In-Folder Scheduling</span>
			</div>

			{/* Controls row — above info banner */}
			<div className={styles.controlsRow}>
				<div className={styles.folderSubheader}>
					<strong>{folderLabel}</strong> — {folderCount} ITEMS
				</div>

				<div className={styles.controlsRight}>
					{/* Timeline navigation — prev / next window */}
					<div className={styles.timelineNav}>
						<button
							type="button"
							className={styles.timelineNavBtn}
							onClick={() => scrollTimeline(-1)}
							title="Previous period"
						>
							<CustomIcon icon="chevron-left" />
						</button>
						<span className={styles.timelineNavLabel}>
							{`${timelineStart.getMonth() + 1}/${timelineStart.getDate()} – ${(() => { const e = addDays(timelineStart, viewDays - 1); return `${e.getMonth() + 1}/${e.getDate()}`; })()}`}
						</span>
						<button
							type="button"
							className={styles.timelineNavBtn}
							onClick={() => scrollTimeline(1)}
							title="Next period"
						>
							<CustomIcon icon="chevron-right" />
						</button>
					</div>

					{/* View density toggle — sliding pill */}
					<div className={styles.viewToggle} ref={viewToggleRef}>
						<div
							className={styles.viewToggleSlider}
							style={{ left: sliderProps.left, width: sliderProps.width }}
						/>
						{Object.entries(VIEW_MODES).map(([key, v]) => (
							<button
								type="button"
								key={key}
								className={`${styles.viewToggleBtn} ${viewMode === key ? styles.viewToggleBtnActive : ''}`}
								onClick={() => setViewMode(key)}
							>
								{v.label}
							</button>
						))}
					</div>
				</div>
			</div>

			{/* Info banner */}
			<div className={styles.infoBanner}>
				<CustomIcon icon="info-circle" style={{ marginRight: '0.5rem' }} />
				You're editing the deck's default schedule. Saving updates every VCL that uses this deck.
				{' '}An active slide with no scheduling will be active at all times.
			</div>

			{loadError && (
				<div className={styles.errorBanner} role="alert">
					<CustomIcon icon="exclamation-triangle" style={{ marginRight: '0.5rem' }} />
					{loadError}
				</div>
			)}

			{saveError && (
				<div className={styles.errorBanner} role="alert">
					<CustomIcon icon="exclamation-triangle" style={{ marginRight: '0.5rem' }} />
					{saveError}
					<button
						type="button"
						className={styles.errorBannerDismiss}
						onClick={() => setSaveError(null)}
						aria-label="Dismiss error"
					>
						<CustomIcon icon="times" />
					</button>
				</div>
			)}

			{/* Top scrollbar mirror — sits above the table */}
			<div className={styles.topScrollbar} ref={topScrollRef}>
				<div style={{ width: totalContentWidth, height: 1 }} />
			</div>

			{/* Table — single overflow:auto container; sticky cells handle fixed columns */}
			<div className={styles.tableWrapper} ref={timelineRef}>

				{/* ── Header row ──────────────────────────────────────────── */}
				<div className={styles.tableHeaderRow}>
					<div className={styles.thActive}>Active</div>
					<div className={styles.thOrder}>Order</div>
					<div className={styles.thName}>Slide Name</div>
					{/* Timeline date labels — no today line here */}
					<div className={styles.thTimeline}>
						{dateColumns.map((col) => (
							<div
								key={col.dateStr}
								className={styles.timelineHeaderCell}
								style={{ width: colDays * colWidth }}
							>
								{col.label}
							</div>
						))}
						{/* Spacer fills remaining width - no empty gap before sticky reset column */}
						<div className={styles.timelineHeaderSpacer} />
					</div>
					<div className={styles.thReset} />
				</div>

				{/* ── File rows ────────────────────────────────────────────── */}
				{files.map((file) => {
					const sched    = scheduleMap[String(file.id)] || { active: true, dateRanges: [], _dirty: false };
					const isOff    = !sched.active;
					const fileType = getFileType(file);

					return (
						<div key={file.id} className={`${styles.fileRow} ${isOff ? styles.fileRowOff : ''}`}>

							{/* ACTIVE — slide-switch toggle, sticky left:0 */}
							<div className={styles.cellActive}>
								<button
									role="switch"
									aria-checked={sched.active}
									aria-label={`${sched.active ? 'Deactivate' : 'Activate'} ${file.file_name || file.name || file.description || 'file'}`}
									className={`${styles.slideSwitch} ${sched.active ? styles.slideSwitchOn : ''}`}
									onClick={() => toggleActive(String(file.id))}
									title={sched.active ? 'Click to deactivate' : 'Click to activate'}
								>
									<span className={styles.slideSwitchThumb} />
								</button>
							</div>

						<div className={styles.cellOrder}>
							{normalizeOrder(file.order)}
						</div>

						<div className={styles.cellName} title={file.file_name || file.name || file.description}>
							<span className={styles.fileIconWrap}>
								<img
									src={getThumbnailPath(file.path)}
									alt=""
									className={styles.fileThumbnail}
									onError={(e) => {
										e.currentTarget.style.display = 'none';
										e.currentTarget.nextSibling.style.display = 'flex';
									}}
								/>
								<span className={styles.fileIconFallback} style={{ display: 'none' }}>
									<CustomIcon icon={fileType === 'video' ? 'play' : 'image'} />
								</span>
								<span className={`${styles.fileTypeBadge} ${fileType === 'video' ? styles.fileTypeBadgeVid : fileType === 'image' ? styles.fileTypeBadgeImg : styles.fileTypeBadgeOther}`}>
									{fileType === 'video' ? 'vid' : fileType === 'image' ? 'img' : 'file'}
								</span>
							</span>
							<span className={`${styles.fileName} ${isOff ? styles.nameOff : ''}`}>
								{file.file_name || file.name || (file.path || '').split('/').pop() || 'Untitled'}
								{isOff && <span className={styles.unscheduledSuffix}> Unscheduled</span>}
							</span>
						</div>

							{/* TIMELINE lane */}
							<div
								className={styles.timelineLane}
							style={{ minWidth: totalWidth }}
								onClick={(e) => {
									if (sched.active) handleTimelineClick(String(file.id), e);
								}}
							>
								{/* Today line + anchor dots */}
								{todayLeft >= 0 && todayLeft <= totalWidth && (
									<>
										<div className={styles.todayLineRow} style={{ left: todayLeft }} />
										<div className={styles.todayDotTop} style={{ left: todayLeft }} />
										<div className={styles.todayDotBottom} style={{ left: todayLeft }} />
									</>
								)}

								{sched.active && sched.dateRanges.length === 0 && (
									<div className={styles.alwaysActiveBadge}>Always Active</div>
								)}

								{/* key must NOT include range dates: they change on every drag step,
								    remounting the bar mid-drag and tearing down its mouse listeners */}
								{sched.active && sched.dateRanges.map((range, idx) => (
									<GanttBar
										key={idx}
										range={range}
										index={idx}
										fileId={String(file.id)}
										colWidth={colWidth}
										timelineStartDate={timelineStart}
										viewDays={viewDays}
										onMove={handleBarMove}
										onResizeLeft={handleBarResizeLeft}
										onResizeRight={handleBarResizeRight}
										onDelete={handleBarDelete}
										onDragEnd={handleDragEnd}
									/>
								))}

								{!sched.active && (
									<div className={styles.unscheduledOverlay}>Unscheduled</div>
								)}
							</div>

							{/* RESET — sticky right:0 */}
							<div className={styles.cellReset}>
								{sched._dirty && (
									<button
										type="button"
										className={styles.resetBtn}
										title="Reset this row"
										onClick={() => resetRow(String(file.id))}
									>
										<CustomIcon icon="undo" />
									</button>
								)}
							</div>
						</div>
					);
				})}
			</div>

			{/* Remove-all-scheduling modal */}
			{removeAllModal && (
				<RemoveAllSchedulingModal
					onUndo={handleRemoveAllUndo}
					onDeactivate={handleRemoveAllDeactivate}
					onProceed={handleRemoveAllProceed}
				/>
			)}
		</div>
	);
};

export default InFolderScheduling;
